import { useEffect, useState } from 'react'
import api from '../api/client'
import Loader from '../components/Loader'
import { DollarSign, TrendingUp, Package, Wallet, ArrowUpRight, AlertTriangle, Percent, Receipt, UserPlus, Landmark } from 'lucide-react'

const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })

function Card({ icon: Icon, label, value, color, t }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow mb-2`}><Icon size={16} className="text-white" /></div>
      <p className="text-lg font-heading font-bold text-gray-900">{value}</p>
      <p className={`text-xs font-medium ${t} mt-0.5`}>{label}</p>
    </div>
  )
}

export default function FinanceiroPainelAdmin() {
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => { api.get('/financeiro/admin/painel').then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setLoading(false)) }, [])
  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader size="lg" text="Carregando painel financeiro..." /></div>
  if (!d) return <p className="text-gray-500">Não foi possível carregar.</p>
  const x = d.dashboard

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-gray-900">Painel Financeiro Executivo</h1>
        <p className="text-sm text-gray-500 mt-0.5">Visão completa da empresa — receitas, comissões, carteira e resultados.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card icon={DollarSign} label="Faturamento Total" value={money(x.faturamento_total)} color="from-emerald-400 to-teal-400" t="text-emerald-700" />
        {x.margem_plataforma !== undefined && x.margem_plataforma !== null && (
          <Card icon={Landmark} label="Margem da Plataforma (Master)" value={money(x.margem_plataforma)} color="from-slate-600 to-gray-800" t="text-gray-800" />
        )}
        <Card icon={TrendingUp} label="Receita Recorrente" value={money(x.receita_recorrente)} color="from-indigo-400 to-violet-400" t="text-indigo-700" />
        <Card icon={Package} label="Receita Produtos" value={money(x.receita_produtos)} color="from-amber-400 to-orange-400" t="text-amber-700" />
        <Card icon={UserPlus} label="Receita Licenças/Adesões" value={money(x.receita_licencas)} color="from-fuchsia-400 to-purple-500" t="text-purple-700" />
        <Card icon={Wallet} label="Saldo em Wallet" value={money(x.saldo_wallet)} color="from-blue-400 to-cyan-400" t="text-blue-700" />
        <Card icon={DollarSign} label="Comissão Prevista" value={money(x.comissao_prevista)} color="from-amber-400 to-orange-400" t="text-amber-700" />
        <Card icon={DollarSign} label="Comissão Liberada" value={money(x.comissao_liberada)} color="from-emerald-400 to-teal-400" t="text-accent-700" />
        <Card icon={ArrowUpRight} label="Saques Pendentes" value={money(x.saques_pendentes)} color="from-rose-400 to-pink-400" t="text-rose-700" />
        <Card icon={ArrowUpRight} label="Saques Pagos" value={money(x.saques_pagos)} color="from-gray-400 to-gray-500" t="text-gray-700" />
        <Card icon={AlertTriangle} label="Inadimplência" value={money(x.inadimplencia)} color="from-rose-400 to-pink-400" t="text-rose-700" />
        <Card icon={Percent} label="Churn" value={`${x.churn}%`} color="from-orange-400 to-red-400" t="text-orange-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center gap-2"><Receipt size={16} /> Contas a Receber (parceiros)</h3>
          <table className="w-full text-sm">
            <thead className="text-gray-400 text-xs uppercase text-left"><tr><th className="py-1">Parceiro</th><th className="py-1 text-right">Previsto</th><th className="py-1 text-right">Recebido</th></tr></thead>
            <tbody>
              {(d.contas_receber || []).map((c, i) => (
                <tr key={i} className="border-t border-gray-50"><td className="py-1.5">{c.parceiro}</td><td className="py-1.5 text-right text-amber-600">{money(c.previsto)}</td><td className="py-1.5 text-right text-emerald-600">{money(c.recebido)}</td></tr>
              ))}
              {(d.contas_receber || []).length === 0 && <tr><td colSpan={3} className="py-3 text-gray-400">Sem dados.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center gap-2"><DollarSign size={16} /> Contas a Pagar</h3>
          <table className="w-full text-sm">
            <tbody>
              {(d.contas_pagar || []).map((c, i) => (
                <tr key={i} className="border-t border-gray-50"><td className="py-1.5">{c.categoria}</td><td className="py-1.5 text-right font-medium">{money(c.valor)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <h3 className="font-heading font-semibold text-gray-900 mb-3">Centro de Resultados (receita por vertical)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {(d.centro_resultados || []).map((c, i) => (
            <div key={i} className="rounded-xl bg-gray-50 p-3">
              <p className="text-xs text-gray-500 capitalize">{(c.vertical || '').replace(/_/g, ' ')}</p>
              <p className="font-heading font-bold text-gray-900">{money(c.receita)}</p>
            </div>
          ))}
          {(d.centro_resultados || []).length === 0 && <p className="text-sm text-gray-400">Sem dados.</p>}
        </div>
      </div>
    </div>
  )
}
